/**
 * Web Auth Provider
 *
 * Manages authentication state for the web application.
 * Simplified version without Electron-specific features.
 */

import { createLogger } from '@ctrl-enter/tools/utils';
import type { Permission, User } from '@ctrl-enter/types';
import React, { createContext, useCallback, useEffect, useState } from 'react';
import { useGenerateTokenQuery } from '../hooks/use-gateway-queries';
import { broadcastAuthState, listenForAuthStateChanges, type ChatAccessInfo } from './auth-sync';
import { isInIframe, openOAuthPopup } from './oauth-popup';
import {
  clearStoredTokens,
  getStoredTokens,
  getValidAccessToken,
  refreshTokens as refreshTokensUtil,
  storeTokens,
  validateTokensWithServer,
} from './token-manager';

const logger = createLogger('web:auth:provider');

type AuthContextType = {
  isAuthenticated: boolean;
  user: User | null;
  isLoading: boolean;
  error: string | null;
  login: (returnTo?: string) => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
  refreshTokens: () => Promise<boolean>;
  hasPermission: (permission: Permission) => boolean;
  refreshUser: () => Promise<boolean>;
  generateWidgetToken: () => Promise<string>;
};

const AuthContext = createContext<AuthContextType | null>(null);

const getApiBaseUrl = (): string => {
  return import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isAuthenticated = user !== null;

  // Widget token query (like desktop)
  const generateTokenQuery = useGenerateTokenQuery();

  /**
   * Listen for auth state changes from extension/other tabs
   */
  useEffect(() => {
    const cleanup = listenForAuthStateChanges((isAuth, newUser, chatAccess) => {
      logger.info('📥 [AUTH-PROVIDER] Received external auth state change', {
        isAuth,
        userEmail: newUser?.email,
        chatAccess,
      });

      if (isAuth && newUser && !user) {
        // External login - update our state
        setUser(newUser);
        setIsLoading(false);
      } else if (!isAuth && user) {
        // External logout - clear our state
        setUser(null);
        setIsLoading(false);
      }
    });

    return cleanup;
  }, [user]);

  /**
   * Listen for token changes from other tabs/contexts
   */
  useEffect(() => {
    const handler = async () => {
      logger.info('🔄 [AUTH-PROVIDER] Auth tokens changed in another context, re-checking...');

      // Re-check auth state
      const tokenResult = getStoredTokens();
      if (tokenResult.success && tokenResult.tokens && !user) {
        // We have tokens but no user - fetch user data
        try {
          const accessToken = await getValidAccessToken();
          if (accessToken) {
            const API_BASE_URL = getApiBaseUrl();
            const { getDeviceId } = await import('../api/client');
            const response = await fetch(`${API_BASE_URL}/auth/me`, {
              headers: {
                Authorization: `Bearer ${accessToken}`,
                'x-app-source': 'enter-ctrl-web',
                'x-device-id': getDeviceId(), // CRITICAL: Include device ID
              },
            });

            if (response.ok) {
              const data = await response.json();
              setUser(data.user);
              logger.info('✅ [AUTH-PROVIDER] User fetched:', data.user.email);

              // Identify user in analytics
              const { identifyUser } = await import('../services/analytics');
              identifyUser(data.user);
            }
          }
        } catch (err) {
          logger.error('❌ [AUTH-PROVIDER] Failed to fetch user:', err);
        }
      } else if (!tokenResult.success && user) {
        // Tokens gone - logout
        setUser(null);
        logger.info('✅ [AUTH-PROVIDER] User cleared');
      }
    };

    window.addEventListener('auth-tokens-changed', handler);
    return () => window.removeEventListener('auth-tokens-changed', handler);
  }, [user]);

  /**
   * Initialize auth state on mount
   */
  useEffect(() => {
    const initAuth = async () => {
      try {
        logger.info('🔄 [AUTH-PROVIDER] Initializing auth state...');

        // Check if we have valid tokens
        const tokenResult = getStoredTokens();
        if (!tokenResult.success || !tokenResult.tokens) {
          logger.info('ℹ️ [AUTH-PROVIDER] No stored tokens found');
          setIsLoading(false);
          return;
        }

        // Validate tokens with server
        const isValid = await validateTokensWithServer();
        if (!isValid) {
          logger.warn('⚠️ [AUTH-PROVIDER] Stored tokens are invalid');
          clearStoredTokens();
          setIsLoading(false);
          return;
        }

        // Fetch user data
        const accessToken = await getValidAccessToken();
        if (!accessToken) {
          logger.warn('⚠️ [AUTH-PROVIDER] Could not get valid access token');
          setIsLoading(false);
          return;
        }

        const API_BASE_URL = getApiBaseUrl();
        const { getDeviceId } = await import('../api/client');
        const response = await fetch(`${API_BASE_URL}/auth/me`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'x-app-source': 'enter-ctrl-web',
            'x-device-id': getDeviceId(), // CRITICAL: Include device ID
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch user data');
        }

        const data = await response.json();
        setUser(data.user);
        logger.info('✅ [AUTH-PROVIDER] User authenticated:', data.user.email);

        // Identify user in analytics
        const { identifyUser } = await import('../services/analytics');
        identifyUser(data.user);
      } catch (err) {
        logger.error('❌ [AUTH-PROVIDER] Error initializing auth:', err);
        setError(err instanceof Error ? err.message : 'Unknown error');
        clearStoredTokens();
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, []);

  /**
   * Initiate OAuth login flow
   */
  const login = useCallback(async (returnTo?: string) => {
    try {
      setIsLoading(true);
      setError(null);

      const API_BASE_URL = getApiBaseUrl();
      logger.info('🔐 [AUTH-PROVIDER] Initiating login flow...', { returnTo });

      // Clear any old device IDs (desktop/previous sessions) before generating new one
      logger.info('🗑️ [AUTH-PROVIDER] Clearing old device IDs...');

      // Clear ALL possible device ID storage locations
      localStorage.removeItem('device-id'); // Old desktop key
      localStorage.removeItem('ctrl-enter-device-id'); // Current web key
      sessionStorage.clear(); // Clear any session storage

      // Get device ID functions
      const { clearDeviceId, getDeviceId } = await import('../api/client');

      // CRITICAL: Clear the in-memory cache FIRST
      clearDeviceId();

      // Small delay to ensure cache is cleared
      await new Promise(resolve => setTimeout(resolve, 100));

      // Now generate fresh device ID (will create new one since cache is cleared)
      const deviceId = getDeviceId();
      logger.info('🔑 [AUTH-PROVIDER] Generated fresh device ID for OAuth:', deviceId);

      // Get OAuth redirect URL from server with platform identifier
      // Backend will choose the correct redirect URI based on platform
      const returnToQuery =
        returnTo && returnTo.startsWith('/') && !returnTo.startsWith('//')
          ? `&returnTo=${encodeURIComponent(returnTo)}`
          : '';
      const response = await fetch(`${API_BASE_URL}/auth/login?platform=web${returnToQuery}`, {
        headers: {
          'x-app-source': 'enter-ctrl-web',
          'x-device-id': deviceId, // Pass device ID to backend for OAuth state
        },
      });

      if (!response.ok) {
        throw new Error('Failed to get login URL');
      }

      const data = await response.json();

      // Check if we're in an iframe (extension sidebar)
      const inIframe = isInIframe();

      if (inIframe) {
        // Open OAuth in popup window
        logger.info('🪟 [AUTH-PROVIDER] Opening OAuth in popup (iframe detected)...');
        const result = await openOAuthPopup(data.redirectUrl);

        if (!result.success) {
          throw new Error(result.error || 'Authentication failed');
        }

        // Store tokens
        logger.info('✅ [AUTH-PROVIDER] OAuth successful, storing tokens...');
        const storeResult = storeTokens(
          result.accessToken!,
          result.refreshToken!,
          result.expiresAt!
        );

        if (!storeResult.success) {
          throw new Error('Failed to store tokens');
        }

        // Set user
        setUser(result.user!);
        setIsLoading(false);

        // Fetch chat access requirements to include in broadcast
        let chatAccess: ChatAccessInfo | undefined;
        try {
          logger.info('🔍 [AUTH-PROVIDER] Fetching chat access requirements...');
          const chatAccessResponse = await fetch(`${API_BASE_URL}/billing/check-chat-access`, {
            headers: {
              Authorization: `Bearer ${result.accessToken}`,
              'x-app-source': 'enter-ctrl-web',
              'x-device-id': deviceId,
            },
          });

          if (chatAccessResponse.ok) {
            const chatAccessData = await chatAccessResponse.json();
            chatAccess = {
              hasAccess: chatAccessData.hasAccess || false,
              requirements: chatAccessData.requirements || [],
              subscriptionStatus: chatAccessData.subscription?.status || 'unknown',
            };
            logger.info('✅ [AUTH-PROVIDER] Chat access requirements:', chatAccess);
          }
        } catch (err) {
          logger.warn('⚠️ [AUTH-PROVIDER] Failed to fetch chat access requirements:', err);
          // Continue without chat access info - will be fetched by ChatAvailabilityProvider
        }

        // Broadcast to extension with chat access info
        broadcastAuthState(true, result.user!, chatAccess);

        logger.info('✅ [AUTH-PROVIDER] Login complete!');
      } else {
        // Normal redirect flow (full browser)
        logger.info('🚀 [AUTH-PROVIDER] Redirecting to OAuth provider...');
        window.location.href = data.redirectUrl;
      }
    } catch (err) {
      logger.error('❌ [AUTH-PROVIDER] Login error:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      setIsLoading(false);
    }
  }, []);

  /**
   * Logout user
   */
  const logout = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      if (user) {
        const API_BASE_URL = getApiBaseUrl();
        const accessToken = await getValidAccessToken();
        const { getDeviceId } = await import('../api/client');

        // Call logout endpoint
        await fetch(`${API_BASE_URL}/auth/logout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
            'x-app-source': 'enter-ctrl-web',
            'x-device-id': getDeviceId(),
          },
          body: JSON.stringify({ userId: user.id }),
        });
      }

      // Clear local state
      clearStoredTokens();
      setUser(null);

      // Clear device ID to ensure fresh session on next login
      const { clearDeviceId } = await import('../api/client');
      clearDeviceId(); // Clears both localStorage and cache
      localStorage.removeItem('device-id'); // Old desktop key
      logger.info('🗑️ [AUTH-PROVIDER] Cleared device ID');

      // Reset analytics identity
      const { resetIdentity } = await import('../services/analytics');
      resetIdentity();

      // Broadcast to extension
      broadcastAuthState(false, null);

      logger.info('✅ [AUTH-PROVIDER] User logged out');
    } catch (err) {
      logger.error('❌ [AUTH-PROVIDER] Logout error:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      // Clear tokens anyway
      clearStoredTokens();
      setUser(null);

      // Reset analytics identity even on error
      const { resetIdentity } = await import('../services/analytics');
      resetIdentity();

      // Broadcast to extension even on error
      broadcastAuthState(false, null);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  /**
   * Get valid access token
   */
  const getAccessToken = useCallback(async () => {
    return getValidAccessToken();
  }, []);

  /**
   * Refresh authentication tokens
   */
  const refreshTokens = useCallback(async (): Promise<boolean> => {
    try {
      // Get stored tokens to extract refresh token
      const tokenResult = getStoredTokens();
      if (!tokenResult.success || !tokenResult.tokens) {
        logger.error('❌ [AUTH-PROVIDER] No stored tokens to refresh');
        return false;
      }

      const result = await refreshTokensUtil(tokenResult.tokens.refreshToken);
      if (result.success && result.tokens) {
        logger.info('✅ [AUTH-PROVIDER] Tokens refreshed successfully');
        return true;
      }
      logger.error('❌ [AUTH-PROVIDER] Failed to refresh tokens:', result.error);
      return false;
    } catch (err) {
      logger.error('❌ [AUTH-PROVIDER] Error refreshing tokens:', err);
      return false;
    }
  }, []);

  /**
   * Check if user has a specific permission
   */
  const hasPermission = useCallback(
    (permission: Permission): boolean => {
      if (!user || !user.permissions) {
        return false;
      }
      return user.permissions.includes(permission);
    },
    [user]
  );

  /**
   * Refresh user data from server
   */
  const refreshUser = useCallback(async (): Promise<boolean> => {
    try {
      const accessToken = await getValidAccessToken();
      if (!accessToken) {
        logger.warn('⚠️ [AUTH-PROVIDER] No valid access token for user refresh');
        return false;
      }

      const API_BASE_URL = getApiBaseUrl();
      const { getDeviceId } = await import('../api/client');
      const response = await fetch(`${API_BASE_URL}/auth/me`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'x-app-source': 'enter-ctrl-web',
          'x-device-id': getDeviceId(), // CRITICAL: Include device ID
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch user data');
      }

      const data = await response.json();
      setUser(data.user);
      logger.info('✅ [AUTH-PROVIDER] User data refreshed:', data.user.email);

      // Update user in analytics
      const { identifyUser } = await import('../services/analytics');
      identifyUser(data.user);

      return true;
    } catch (err) {
      logger.error('❌ [AUTH-PROVIDER] Error refreshing user:', err);
      return false;
    }
  }, []);

  /**
   * Generate widget token for WorkOS widgets
   * Uses React Query client like desktop for proper header management
   */
  const generateWidgetToken = useCallback(async (): Promise<string> => {
    logger.info('🔷 [AUTH-PROVIDER] generateWidgetToken called', {
      hasUser: !!user,
      userId: user?.id,
      userEmail: user?.email,
    });

    if (!user?.id) {
      logger.error('❌ [AUTH-PROVIDER] No user ID available for widget token generation');
      throw new Error('User ID is required to generate a token');
    }

    try {
      logger.info('🔷 [AUTH-PROVIDER] Calling backend /auth/widget endpoint via tsrClient...');
      const result = await generateTokenQuery.refetch();

      logger.info('🔷 [AUTH-PROVIDER] Backend response:', {
        success: !!result.data,
        hasBody: !!result.data?.body,
        hasToken: !!result.data?.body?.widgetToken,
        tokenLength: result.data?.body?.widgetToken?.length || 0,
        status: result.status,
        error: result.error ? String(result.error) : null,
      });

      if (!result.data?.body?.widgetToken) {
        logger.error('❌ [AUTH-PROVIDER] No widget token in response');
        throw new Error('Failed to generate widget token: empty response');
      }

      logger.info('✅ [AUTH-PROVIDER] Widget token generated successfully');
      return result.data.body.widgetToken;
    } catch (err) {
      logger.error('❌ [AUTH-PROVIDER] Error generating widget token:', err);
      throw err;
    }
  }, [user?.id, generateTokenQuery]);

  const value: AuthContextType = {
    isAuthenticated,
    user,
    isLoading,
    error,
    login,
    logout,
    getAccessToken,
    refreshTokens,
    hasPermission,
    refreshUser,
    generateWidgetToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Hook to use auth context
 */
export const useAuth = (): AuthContextType => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
